import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { AlertTriangle, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { colors, spacing, borderRadius } from '../styles/globalStyles';

const getInputPropsForKey = (key) => {
  const k = (key || '').toLowerCase();
  if (/\b(phone|mobile|contact|number)\b/.test(k)) return { type: 'tel', autoComplete: 'tel' };
  if (/\b(email|e-mail)\b/.test(k)) return { type: 'email', autoComplete: 'email' };
  return { type: 'text', autoComplete: 'off' };
};

const InfoItemRow = ({ item, index, onUpdate, onDelete, onMoveUp, onMoveDown, onSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editKey, setEditKey] = useState(item.key);
  const [editValue, setEditValue] = useState(item.value);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSave = async () => {
    if (!editKey.trim() || !editValue.trim()) {
      setErrorMessage('Please fill in both field name and value');
      setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    try {
      setErrorMessage(null);
      await onUpdate({
        key: editKey.trim(),
        value: editValue.trim()
      });
      setIsEditing(false);
      onSaved?.();
    } catch (error) {
      console.error('Error updating item:', error);
      // Clean error message - remove URLs/IPs
      let cleanError = 'Failed to update item. Please try again.';
      if (error?.response?.data?.message) {
        cleanError = error.response.data.message;
        cleanError = cleanError.replace(/https?:\/\/[^\s]+/gi, '');
        cleanError = cleanError.replace(/\d+\.\d+\.\d+\.\d+/g, '');
        cleanError = cleanError.trim() || 'Failed to update item. Please try again.';
      }
      setErrorMessage(cleanError);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const handleCancel = () => {
    setEditKey(item.key);
    setEditValue(item.value);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await onDelete();
      } catch (error) {
        console.error('Error deleting item:', error);
        // Clean error message - remove URLs/IPs
        let cleanError = 'Failed to delete item. Please try again.';
        if (error?.response?.data?.message) {
          cleanError = error.response.data.message;
          cleanError = cleanError.replace(/https?:\/\/[^\s]+/gi, '');
          cleanError = cleanError.replace(/\d+\.\d+\.\d+\.\d+/g, '');
          cleanError = cleanError.trim() || 'Failed to delete item. Please try again.';
        }
        setErrorMessage(cleanError);
        setTimeout(() => setErrorMessage(null), 5000);
      }
    }
  };

  const toggleVisibility = async () => {
    try {
      await onUpdate({ isVisible: !item.isVisible });
      onSaved?.();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      // Clean error message - remove URLs/IPs
      let cleanError = 'Failed to update visibility. Please try again.';
      if (error?.response?.data?.message) {
        cleanError = error.response.data.message;
        cleanError = cleanError.replace(/https?:\/\/[^\s]+/gi, '');
        cleanError = cleanError.replace(/\d+\.\d+\.\d+\.\d+/g, '');
        cleanError = cleanError.trim() || 'Failed to update visibility. Please try again.';
      }
      setErrorMessage(cleanError);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-2 p-3 bg-white rounded-lg border-2 transition-colors duration-150 ${
            snapshot.isDragging
              ? 'border-blue-500 shadow-md z-10'
              : 'border-gray-200 hover:border-gray-300'
          } ${!item.isVisible ? 'opacity-60' : ''}`}
        >
          {!isEditing ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  {...provided.dragHandleProps}
                  className="cursor-grab active:cursor-grabbing flex items-center justify-center w-11 h-11 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-800">{item.key}:</p>
                    <p className="text-gray-600">{item.value}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {onMoveUp && (
                  <button
                    onClick={onMoveUp}
                    className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-lg transition"
                    title="Move up"
                    aria-label="Move up"
                  >
                    <ChevronUp size={20} />
                  </button>
                )}
                {onMoveDown && (
                  <button
                    onClick={onMoveDown}
                    className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-lg transition"
                    title="Move down"
                    aria-label="Move down"
                  >
                    <ChevronDown size={20} />
                  </button>
                )}
                <button
                  onClick={toggleVisibility}
                  className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-lg transition text-lg"
                  title={item.isVisible ? 'Visible to public' : 'Hidden from public'}
                  aria-label={item.isVisible ? 'Hide from public' : 'Show to public'}
                >
                  {item.isVisible ? '👁️' : '👁️‍🗨️'}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-11 h-11 flex items-center justify-center hover:bg-blue-100 text-blue-600 rounded-lg transition"
                  title="Edit"
                  aria-label="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={handleDelete}
                  className="w-11 h-11 flex items-center justify-center hover:bg-red-100 text-red-600 rounded-lg transition"
                  title="Delete"
                  aria-label="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              {errorMessage && (
                <div
                  style={{
                    padding: spacing.sm,
                    backgroundColor: `${colors.danger}10`,
                    border: `1px solid ${colors.danger}`,
                    borderRadius: borderRadius.md,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                  }}
                >
                  <AlertTriangle size={16} color={colors.danger} />
                  <p style={{ margin: 0, fontSize: '12px', color: colors.danger, fontWeight: '600' }}>{errorMessage}</p>
                </div>
              )}
              <input
                type="text"
                value={editKey}
                onChange={(e) => setEditKey(e.target.value)}
                style={{
                  width: '100%',
                  padding: spacing.md,
                  minHeight: '44px',
                  border: `1px solid ${colors.gray[300]}`,
                  borderRadius: borderRadius.md,
                  fontSize: '16px',
                }}
                placeholder="Field Name"
                autoFocus
                autoComplete="off"
              />
              <input
                {...getInputPropsForKey(editKey)}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  else if (e.key === 'Escape') handleCancel();
                }}
                style={{
                  width: '100%',
                  padding: spacing.md,
                  minHeight: '44px',
                  border: `1px solid ${colors.gray[300]}`,
                  borderRadius: borderRadius.md,
                  fontSize: '16px',
                }}
                placeholder="Value"
              />
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <button
                  onClick={handleSave}
                  style={{
                    flex: 1,
                    padding: spacing.md,
                    minHeight: '44px',
                    backgroundColor: colors.success,
                    color: colors.white,
                    border: 'none',
                    borderRadius: borderRadius.md,
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: spacing.md,
                    minHeight: '44px',
                    backgroundColor: colors.gray[400],
                    color: colors.white,
                    border: 'none',
                    borderRadius: borderRadius.md,
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default InfoItemRow;
